import React, { FC, useMemo } from 'react';
import { format } from 'date-fns';
import { roundValue } from '../Analytics';
import { Grid, styled as muiStyled } from '@material-ui/core';
import { Metric } from '@/components/pages/blockSchedule/metrics/Metric';
import { BookingStat } from '@/components/pages/blockSchedule/BlockSchedulePage';
import styled from 'styled-components';
import SummaryCapacityBar from './SummaryCapacityBar';

const MetricsSection = styled.section`
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.02));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1);
  padding: 16px;
  overflow: hidden;
  margin-bottom: 16px;
`;

const MetricsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const MetricsTitle = styled.h2`
  font-size: 16px;
  font-weight: 500;
  margin: 0;
  color: rgba(255, 255, 255, 0.95);
`;

const MonthBadge = styled.span`
  font-size: 13px;
  line-height: 1.2;
  padding: 6px 12px;
  border-radius: 9999px;
  background: rgba(34, 211, 238, 0.1);
  color: rgba(224, 242, 254, 1);
  border: 1px solid rgba(103, 232, 249, 0.25);
`;

type MetricProps = { statistics: Map<string, BookingStat>; loading: boolean; date?: Date };

export const Metrics: FC<MetricProps> = ({ statistics, loading, date }) => {
  const formattedValues = useMemo(() => {
    const totalCapacity = statistics.get('totalCapacity');
    const allocatedBlocksPercentage = statistics.get('allocatedBlocksPercentage');
    const allocatedBlocks = statistics.get('allocatedBlocks');
    const scheduledUtilizationPercentage = statistics.get('scheduledUtilizationPercentage');
    const scheduledUtilization = statistics.get('scheduledUtilization');
    const actualUtilizationPercentage = statistics.get('actualUtilizationPercentage');
    const actualUtilization = statistics.get('actualUtilization');

    return {
      totalCapacity: formatTotalCapacity(totalCapacity?.value),
      allocatedBlocksPercentage: formatValue(allocatedBlocksPercentage?.value, allocatedBlocksPercentage?.unit),
      allocatedBlocks: formatValue(allocatedBlocks?.value, allocatedBlocks?.unit),
      scheduledUtilizationPercentage: formatValue(
        scheduledUtilizationPercentage?.value,
        scheduledUtilizationPercentage?.unit
      ),
      scheduledUtilization: formatValue(scheduledUtilization?.value, scheduledUtilization?.unit),
      actualUtilizationPercentage: formatValue(actualUtilizationPercentage?.value, actualUtilizationPercentage?.unit),
      actualUtilization: formatValue(actualUtilization?.value, actualUtilization?.unit),
    };
  }, [statistics]);

  const monthLabel = date ? format(date, 'MMMM YYYY') : format(new Date(), 'MMMM YYYY');

  return (
    <MetricsSection>
      <MetricsHeader>
        <MetricsTitle>Monthly Summary</MetricsTitle>
        <MonthBadge>Monthly ({monthLabel})</MonthBadge>
      </MetricsHeader>
      <MetricsContainer container spacing={4}>
        <Grid item xs={12} md={3}>
          <Metric
            title={'Capacity'}
            tooltip={'Total available OR hours for the center in this period (all rooms combined).'}
            value={formattedValues.totalCapacity}
            loading={loading}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <Metric
            title={'Overall Allocated'}
            tooltip={
              'The percentage of total OR capacity assigned across all physicians. (= Total Allocated ÷ Total Capacity × 100)'
            }
            valueNode={(() => {
              const pct = Number(statistics.get('allocatedBlocksPercentage')?.value || 0);
              const left = pct > 100 ? <Bang>!</Bang> : <Pie percent={pct} color={'#3D4A7A'} />;
              return (
                <Inline>
                  {left}
                  <Big>{Math.round(pct)}%</Big>
                </Inline>
              );
            })()}
            subvalueNode={
              <span>
                {roundValue(statistics.get('allocatedBlocks')?.value || 0)}/
                {roundValue(statistics.get('totalCapacity')?.value || 0)} h
              </span>
            }
            loading={loading}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <Metric
            title={'Overall Scheduled'}
            tooltip={
              'The percentage of allocated time across all physicians that has cases scheduled. (= Total Time for Cases Scheduled ÷ Total Allocated × 100)'
            }
            valueNode={(() => {
              const pct = Number(statistics.get('scheduledUtilizationPercentage')?.value || 0);
              const left = pct > 100 ? <Bang>!</Bang> : <Pie percent={pct} color={'#00A7F7'} />;
              return (
                <Inline>
                  {left}
                  <Big>{Math.round(pct)}%</Big>
                </Inline>
              );
            })()}
            subvalueNode={
              <span>
                {roundValue(statistics.get('scheduledUtilization')?.value || 0)}/
                {roundValue(statistics.get('allocatedBlocks')?.value || 0)} h
              </span>
            }
            loading={loading}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <Metric
            title={'Overall Used'}
            tooltip={
              'The percentage of allocated time across all physicians that was actually used for surgeries (measured wheels in → wheels out). (= Total Wheels Time ÷ Total Allocated × 100)'
            }
            valueNode={(() => {
              const pct = Number(statistics.get('actualUtilizationPercentage')?.value || 0);
              const left = pct > 100 ? <Bang>!</Bang> : <Pie percent={pct} color={'#99E6B3'} />;
              return (
                <Inline>
                  {left}
                  <Big>{Math.round(pct)}%</Big>
                </Inline>
              );
            })()}
            subvalueNode={
              <span>
                {roundValue(statistics.get('actualUtilization')?.value || 0)}/
                {roundValue(statistics.get('allocatedBlocks')?.value || 0)} h
              </span>
            }
            loading={loading}
          />
        </Grid>

        <Grid item xs={12} md={12}>
          <SummaryCapacityBar
            capacityHours={statistics.get('totalCapacity')?.value || 0}
            allocatedHours={statistics.get('allocatedBlocks')?.value || 0}
            scheduledHours={statistics.get('scheduledUtilization')?.value || 0}
            actualHours={statistics.get('actualUtilization')?.value || 0}
          />
        </Grid>
      </MetricsContainer>
    </MetricsSection>
  );
};

export const MetricsContainer = muiStyled(Grid)(({ theme }) => ({
  margin: theme.spacing(0, 0, 0, 0),
  width: '100%',
  [theme.breakpoints.down('md')]: {
    margin: theme.spacing(0, 0, 0, 0),
  },
}));

const formatValue = (value: number | null | undefined, unit: string | undefined | null): string | null => {
  if (!value) {
    return null;
  }

  if (unit && ['hours', 'hrs'].includes(unit)) {
    return `${roundValue(value)} Hrs`;
  }
  if (unit && ['percentage', '%'].includes(unit)) {
    return `${roundValue(value)}%`;
  }
  return String(value);
};

const formatTotalCapacity = (value: number | null | undefined): string | null => {
  if (value == null) {
    return null;
  }
  return `${Math.round(value)} h`;
};

const Pie = ({ percent, color }: { percent: number; color: string }) => {
  const p = Math.max(0, Math.min(100, Number(percent) || 0));
  const bg = 'rgba(255,255,255,0.12)';
  const gradient = `conic-gradient(${color} 0% ${p}%, ${bg} ${p}% 100%)`;
  return <div style={{ width: 24, height: 24, borderRadius: '50%', background: gradient }} />;
};

const Inline = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const Big = styled.span`
  font-weight: 800;
  font-size: 22px;
`;

const Bang = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 20px;
`;

const MonthPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  font-size: 0.9rem;
  width: fit-content;
`;
